import { Box, Button, HStack, Stack, useSteps, VStack } from "@chakra-ui/react"
import { StepTracker } from "./StepTracker"
import { PercentTracker } from "./PercentTracker";
import { StepDisplayModel, StepStateModel } from "../models/StepModel";
import StepContainer from "./StepContainer";
import { useEffect, useState } from "react";
import { StepDetailsBaseProps } from "../models/StepDetailsBaseProps";
import { SummaryStep } from "./steps/SummaryStep";

export interface StepWizardProps {
  tracker?: 'steps' | 'percent',
  orientation?: 'horizontal' | 'vertical',
  colorScheme?: 'red' | 'orange' | 'yellow' | 'green' | 'blue',
  wizardSteps: StepDisplayModel[],
  onFinish: (progress: StepStateModel[]) => Promise<void>,
  onExitWithoutSaving: () => void,
}

interface StepWizardProgressState {
  stepsSavedState: StepStateModel[],
}

export const StepWizard = (props: StepWizardProps) => {
    const { tracker = 'steps', 
      orientation = 'horizontal', 
      colorScheme = 'blue', 
      wizardSteps,
      onFinish, 
      onExitWithoutSaving
    } = props; //default to steps, blue, and horizontal if no values provided

    const { activeStep, goToPrevious, goToNext, setActiveStep, getStatus } = useSteps({ index: 0, count: wizardSteps.length });
    const [progressState, setProgressState] = useState<StepWizardProgressState>({
        stepsSavedState: []
    });
    const [isStepValid, setIsStepValid] = useState<boolean>(false); // to track if active step validation passed
    // steps is zero based, so activeStep is 0 for the first step, 1 for the second step, etc.
    // isLastStep = steps.length - 1 === activeStep; 
    const isFinishStep = wizardSteps.length === activeStep;
    const isFirstStep = activeStep === 0;

    const stepOptions = wizardSteps[activeStep]?.stepOptions;
    const stepSavedOptionValue = progressState?.stepsSavedState[activeStep]?.selectedOptionValue; // get the selected option value from the saved state
  
    let defaultStepOption: string | null = null; 
    if(stepOptions && stepOptions.length > 0) {
      defaultStepOption = stepOptions?.[0]?.optionValue?.toString()
    }
    if(stepSavedOptionValue) {
      defaultStepOption = stepSavedOptionValue?.toString(); // convert to string
    }

    const [selectedStepOption, setSelectedStepOption] = useState<string | null>(defaultStepOption);
    console.log({activeStep, selectedStepOption, stepSavedOptionValue, stepOptions, progressState});

    useEffect(() => { 
      console.log('useEffect Active step changed:', {activeStep, selectedStepOption, defaultStepOption})
      if(selectedStepOption !== defaultStepOption) {
        setSelectedStepOption(defaultStepOption); // set the default option value
      }
      setIsStepValid(false); // reset the step valid state when the active step changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [activeStep]); // set the default option value when the active step changes

    const finishWizard = async (activeStep: number) => {
        console.log('Finish Wizard:', {activeStep, progressState })
        await onFinish(progressState?.stepsSavedState); // call the finish function passed from the parent component
    }

    // save the active step state to the progress state, will be passed in deep to child step specific component
    const saveActiveStepState = (activeStep: number, progress: StepStateModel) => {
        const newProgressState = [...progressState.stepsSavedState];
        newProgressState[activeStep] = {...progress};  
        setProgressState({ stepsSavedState: newProgressState });
        console.log('Save Active step:', {activeStep, newProgressState})
    }

    const discardActiveStepState = (activeStep: number) => {
        const newProgressState = [...progressState.stepsSavedState];
        newProgressState.length = activeStep; // remove all steps after the active step
        setProgressState({ stepsSavedState: newProgressState });
        console.log('Discarded step state:', activeStep)
    }

    // handle next step click
    const handleNext = () => {
      // const progress: StepStateModel = {
      //   stepIdx: activeStep,
      //   selectedOptionValue: Number(selectedStepOption), // convert to number
      //   priorStepIdx: wizardSteps[activeStep].priorStepIdx,
      //   selectedItems: [], // this will be used to store the selected items from the step
      // };

      // saveActiveStepState(activeStep, progress);
      if (!isStepValid) {
        alert('Please complete the step before proceeding to the next step.');
        return;
      }
      goToNext();
    }

    // handle previous step click
    const handlePrevious = () => {
      if (isFirstStep) {
        // handle first step action
      } else {
        discardActiveStepState(activeStep);
        goToPrevious();
      }
    }

    const handleFinishWizard = async () => {
      if (isFinishStep) {
        // handle finish action
        alert('Finish action');
        await finishWizard(activeStep);
      } else {
        // premature step
      }
    }

    const handleExitWithoutSaving = () => {
      setActiveStep(0);
      discardActiveStepState(0);
      onExitWithoutSaving();
    }
 

  return (
    <Stack 
      spacing={4} 
      direction={orientation === 'vertical' ? 'row' : 'column'} // if vertical change container direction to fit content
    >
      { tracker === 'steps' && 
        <StepTracker 
          steps={wizardSteps} 
          active={activeStep} 
          orientation={orientation} 
          height={orientation === 'vertical' ? '400px' : undefined} // if vertical set a height value
          colorScheme={colorScheme}
          size={orientation === 'vertical' ? 'sm' : 'lg'} // if vertical set a size value
        /> 
      }
      { tracker === 'percent' && 
        <PercentTracker 
          active={activeStep} 
          total={wizardSteps.length}
          colorScheme={colorScheme}
        /> 
      }

      <VStack direction={'column'}>
        <Box height={500} overflow={'auto'}>

          {/* display the current step component */}
          { wizardSteps[activeStep] && 
            <StepContainer
                currentStepIdx={activeStep}
                totalSteps={wizardSteps.length }
                title={wizardSteps[activeStep].title}
                description={wizardSteps[activeStep].description}
                stepOptions={wizardSteps[activeStep].stepOptions}
                selectedStepOptionValue = {selectedStepOption}
                setSelectedStepOption = {setSelectedStepOption}
                saveActiveStepState={saveActiveStepState}
                setIsStepValid={setIsStepValid}
                stepsSavedState={progressState.stepsSavedState} 
                >
                  {wizardSteps[activeStep].component as React.ReactElement<StepDetailsBaseProps>}
            </StepContainer>
          }

          { isFinishStep && 
            <>
              <SummaryStep 
                  saveActiveStepState={function (activeStep: number, progress: StepStateModel): void {
                  throw new Error("Function not implemented."); } } 
                stepsSavedState={progressState.stepsSavedState} 
                title={"Summary"} 
                stepIdx={wizardSteps.length}
                setIsStepValid={setIsStepValid}  />
            </>
          }
        </Box>
        <HStack mt={5}>
          <Button onClick={()=>handlePrevious()} disabled={isFirstStep}>Prev Step</Button>
          { !isFinishStep && 
            <Button onClick={handleNext} colorScheme={colorScheme}>Next Step</Button> }
            
          { isFinishStep && 
            <Button onClick={handleFinishWizard} colorScheme={colorScheme}>Finish/Create</Button> }

          <Button onClick={handleExitWithoutSaving} >Cancel/Exit</Button>
        </HStack>
      </VStack>
    </Stack>
  )
}
